import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import axios from "axios";
import { TextField, Grid, MenuItem, Paper } from "@mui/material";
import ModalidadeSelect from "./ModalidadeSelect";
import InputMask from "react-input-mask";
import { api } from "@api/api";

const EscolaCampos = forwardRef((props, ref) => {
  const {
    onDataChange,
    onFileChange,
    escolaSelecionada,
    setEscolaSelecionada,
    novoNomeEscola,
    setNovoNomeEscola,
  } = props;

  const [escolas, setEscolas] = useState([]);

  const [dadosEscola, setDadosEscola] = useState({
    rua: "",
    bairro: "",
    municipio: "",
    municipioId: "",
    cep: "",
    numero: "",
    contato: "",
    email: "",
  });

  const [modalidade, setModalidade] = useState("");
  const [anosSelecionados, setAnosSelecionados] = useState([]);
  const [isEditavel, setIsEditavel] = useState(true);
  const [listCities, setListCities] = useState([]);
  const [municipioDisplay, setMunicipioDisplay] = useState("");

  const modalidadeSelectRef = useRef();

  const formatarCEP = (cep) => {
    return cep
      .replace(/\D/g, "")
      .replace(/(\d{5})(\d)/, "$1-$2")
      .replace(/(-\d{3})\d+?$/, "$1");
  };

  const formatarTelefone = (telefone) => {
    return telefone
      .replace(/\D/g, "")
      .replace(/(\d{2})(\d)/, "($1) $2")
      .replace(/(\d{5})(\d)/, "$1-$2")
      .replace(/(-\d{4})\d+?$/, "$1");
  };

  useImperativeHandle(ref, () => ({
    limparCamposInternos: () => {
      setDadosEscola({
        rua: "",
        bairro: "",
        municipio: "",
        municipioId: "",
        cep: "",
        numero: "",
        contato: "",
        email: "",
      });

      setModalidade("");
      setAnosSelecionados([]);
      setIsEditavel(true);
      setEscolaSelecionada("");
      setNovoNomeEscola("");

      if (modalidadeSelectRef.current) {
        modalidadeSelectRef.current.limparCamposInternos();
      }
    },
  }));

  useEffect(() => {
    const fetchCities = async () => {
      try {
        const res = await axios.get(api.listCitiesWithSre(), {
          withCredentials: true,
        });

        setListCities(res.data || []);
      } catch (err) {
        console.error("Erro ao buscar cidades:", err);
      }
    };

    fetchCities();
  }, []);

  useEffect(() => {
    fetchEscolas();
  }, []);

  useEffect(() => {
    const nomeEscola =
      escolaSelecionada === "Nova Escola" ? novoNomeEscola : escolaSelecionada;

    onDataChange({
      ...dadosEscola,
      nome: nomeEscola,
      modalidade,
      anosSelecionados,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dadosEscola,
    escolaSelecionada,
    novoNomeEscola,
    modalidade,
    anosSelecionados,
  ]);

  const fetchEscolas = async () => {
    try {
      const response = await axios.get(api.listAllSchools(), {
        withCredentials: true,
      });

      const escolasUnicas = response.data.reduce((acc, escola) => {
        if (!acc.some((e) => e.nome === escola.nome)) {
          acc.push(escola);
        }

        return acc;
      }, []);

      setEscolas(escolasUnicas);
    } catch (error) {
      console.error("Erro ao buscar escolas:", error);
    }
  };

  const handleEscolaChange = (event) => {
    const nomeEscola = event.target.value;

    setEscolaSelecionada(nomeEscola);

    if (nomeEscola === "Nova Escola") {
      setNovoNomeEscola("");

      setDadosEscola({
        rua: "",
        bairro: "",
        municipio: "",
        municipioId: "",
        cep: "",
        numero: "",
        contato: "",
        email: "",
      });

      setIsEditavel(true);
    } else {
      const escolaEncontrada = escolas.find(
        (escola) => escola.nome === nomeEscola,
      );

      if (escolaEncontrada) {
        setDadosEscola({
          rua: escolaEncontrada.rua || "",
          bairro: escolaEncontrada.bairro || "",
          municipio: escolaEncontrada.municipio || "",
          municipioId: escolaEncontrada.city_id || "",
          cep: escolaEncontrada.cep || "",
          numero: escolaEncontrada.numero ?? "",
          contato: escolaEncontrada.contato || "",
          email: escolaEncontrada.email || "",
        });

        setIsEditavel(false);
      }
    }
  };

  const handleNovoNomeEscolaChange = (event) => {
    setNovoNomeEscola(event.target.value);
  };

  const handleModalidadeChange = (newModalidade) => {
    setModalidade(newModalidade);

    onDataChange({
      ...dadosEscola,
      modalidade: newModalidade,
    });
  };

  const handleCepChange = (e) => {
    const cepFormatado = formatarCEP(e.target.value);

    setDadosEscola({
      ...dadosEscola,
      cep: cepFormatado,
    });
  };

  const handleContatoChange = (e) => {
    const contatoFormatado = formatarTelefone(e.target.value);

    setDadosEscola({
      ...dadosEscola,
      contato: contatoFormatado,
    });
  };

  const handleCidadeChange = (e) => {
    const cidadeId = Number(e.target.value);

    const cidadeSelecionada = listCities.find((c) => c.id === cidadeId);

    setDadosEscola({
      ...dadosEscola,
      municipio: cidadeSelecionada ? cidadeSelecionada.name : "",
      municipioId: cidadeId,
    });
  };

  return (
    <div>
      <Paper elevation={3} sx={{ padding: 3, borderRadius: 2 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              size="medium"
              label="Nome"
              name="nome"
              value={escolaSelecionada}
              onChange={handleEscolaChange}
              variant="outlined"
              sx={{
                backgroundColor: "#f5f5f5",
                borderRadius: 1,
              }}
            >
              <MenuItem value="Nova Escola">Nova Escola</MenuItem>

              {escolas.map((escola) => (
                <MenuItem key={escola.id} value={escola.nome}>
                  {escola.nome}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {escolaSelecionada === "Nova Escola" && (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="medium"
                label="Novo Nome da Escola"
                name="novoNomeEscola"
                value={novoNomeEscola}
                onChange={handleNovoNomeEscolaChange}
                variant="outlined"
                sx={{
                  backgroundColor: "#f5f5f5",
                  borderRadius: 1,
                }}
              />
            </Grid>
          )}

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="medium"
              label="Rua"
              name="rua"
              value={dadosEscola.rua}
              onChange={(e) =>
                setDadosEscola({
                  ...dadosEscola,
                  rua: e.target.value,
                })
              }
              disabled={!isEditavel}
              variant="outlined"
              sx={{ backgroundColor: "#f5f5f5", borderRadius: 1 }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="medium"
              label="Bairro"
              name="bairro"
              value={dadosEscola.bairro}
              onChange={(e) =>
                setDadosEscola({
                  ...dadosEscola,
                  bairro: e.target.value,
                })
              }
              disabled={!isEditavel}
              variant="outlined"
              sx={{ backgroundColor: "#f5f5f5", borderRadius: 1 }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              size="medium"
              label="Município"
              name="municipio"
              value={dadosEscola.municipioId || ""}
              onChange={handleCidadeChange}
              disabled={!isEditavel}
              variant="outlined"
              sx={{ backgroundColor: "#f5f5f5", borderRadius: 1 }}
            >
              {listCities.map((cidade) => (
                <MenuItem key={cidade.id} value={cidade.id}>
                  {cidade.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <InputMask
              mask="99999-999"
              value={dadosEscola.cep}
              onChange={handleCepChange}
              disabled={!isEditavel}
            >
              {() => (
                <TextField
                  fullWidth
                  size="medium"
                  label="CEP"
                  name="cep"
                  variant="outlined"
                  sx={{ backgroundColor: "#f5f5f5", borderRadius: 1 }}
                />
              )}
            </InputMask>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="medium"
              label="Número"
              name="numero"
              value={
                dadosEscola.numero === 0
                  ? "0"
                  : dadosEscola.numero
                    ? dadosEscola.numero.toString()
                    : ""
              }
              onChange={(e) =>
                setDadosEscola({
                  ...dadosEscola,
                  numero: e.target.value === "" ? "" : Number(e.target.value),
                })
              }
              disabled={!isEditavel}
              variant="outlined"
              sx={{ backgroundColor: "#f5f5f5", borderRadius: 1 }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <InputMask
              mask="(99) 99999-9999"
              value={dadosEscola.contato}
              onChange={handleContatoChange}
              disabled={!isEditavel}
            >
              {() => (
                <TextField
                  fullWidth
                  size="medium"
                  label="Contato"
                  name="contato"
                  variant="outlined"
                  sx={{ backgroundColor: "#f5f5f5", borderRadius: 1 }}
                />
              )}
            </InputMask>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="medium"
              label="E-mail"
              name="email"
              value={dadosEscola.email}
              onChange={(e) =>
                setDadosEscola({
                  ...dadosEscola,
                  email: e.target.value,
                })
              }
              disabled={!isEditavel}
              variant="outlined"
              sx={{ backgroundColor: "#f5f5f5", borderRadius: 1 }}
            />
          </Grid>

          <Grid item xs={12}>
            <ModalidadeSelect
              ref={modalidadeSelectRef}
              onFileChange={onFileChange}
              onModalidadeChange={(newModalidade) => {
                setModalidade(newModalidade);

                onDataChange({
                  ...dadosEscola,
                  modalidade: newModalidade,
                  anosSelecionados,
                });
              }}
              modalidade={modalidade}
              onDataChange={(data) => {
                setAnosSelecionados(data.anosSelecionados);

                onDataChange({
                  ...dadosEscola,
                  modalidade: data.modalidade,
                  anosSelecionados: data.anosSelecionados,
                });
              }}
            />
          </Grid>
        </Grid>
      </Paper>
    </div>
  );
});

export default EscolaCampos;
